"use client";

import {
  type ReactNode,
  type HTMLAttributes,
  useEffect,
  useRef,
  type FC,
  type Ref,
} from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import { useKeys, useUuid } from "../../hooks";
import { Portal } from "../Portal";
import { Backdrop, type BackdropProps } from "../Backdrop";
import { Text } from "../Text";
import { IconButton, type IconButtonProps } from "../IconButton";
import type { EdgesProps } from "../../props";
import { Edges } from "../Edges";
import { Spacing } from "../Spacing";

import { elevations } from "../../theme/css/elevations.css";
import {
  closeStyle,
  containerStyle,
  flexStyle,
  modalContainerStyle,
  modalStyle,
  titleStyle,
} from "./Modal.css";

export type ModalProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSubmit" | "title"
> &
  RecipeVariants<typeof containerStyle> &
  RecipeVariants<typeof modalContainerStyle> &
  EdgesProps & {
    title?: ReactNode;
    closeable?: boolean;
    closeDisabled?: boolean;
    escape?: boolean;
    onClose?: () => void;
    children?: ReactNode;
    description?: string;
    backdropVariant?: BackdropProps["variant"];
    onBackdropClick?: () => void;
    closeButtonSize?: IconButtonProps["size"];
    hideOverflow?: BackdropProps["hideOverflow"];
    overflowHidden?: boolean;
    closeButtonClassName?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * Modal component that is displayed on top of the content.
 * @param {ModalProps} props - The props for the Modal component
 * @returns {JSX.Element} The Modal component
 */
export const Modal: FC<ModalProps> = ({
  title,
  leading,
  trailing,
  closeable = false,
  closeDisabled = false,
  escape: escapable = true,
  placement = "top",
  variant = "default",
  size = "default",
  padding = "default",
  applyPadding = true,
  overflowHidden = true,
  description,
  backdropVariant = "default",
  hideOverflow,
  closeButtonSize = "xs",
  onClose,
  onBackdropClick,
  children,
  closeButtonClassName,
  className,
  ref,
  ...props
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const titleID = useUuid();
  const descriptionID = useUuid();

  useKeys("Escape", () => {
    escapable && onClose?.();
  });

  useEffect(() => {
    // Focus the modal when it opens
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  const handleCloseClick = () => {
    onClose?.();
  };

  return (
    <Portal>
      <Backdrop
        variant={backdropVariant}
        onClick={onBackdropClick}
        hideOverflow={hideOverflow}
      >
        <section
          className={mergeClasses(containerStyle({ size, placement }))}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleID : undefined}
          aria-describedby={description ? descriptionID : undefined}
          ref={modalRef}
          tabIndex={-1}
        >
          <div
            ref={ref}
            className={mergeClasses(
              modalStyle,
              modalContainerStyle({
                size,
                placement,
                variant,
                padding,
                applyPadding,
                overflowHidden,
              }),
              elevations.l,
              className,
            )}
            {...props}
          >
            {closeable && (
              <IconButton
                className={mergeClasses(
                  closeStyle({
                    padding: padding === "none" ? "default" : "parent",
                  }),
                  closeButtonClassName,
                )}
                icon="closeSmall"
                size={closeButtonSize}
                onClick={handleCloseClick}
                variant="opaque"
                border="default"
                disabled={closeDisabled}
                aria-label="Close modal"
                rounded
              />
            )}
            <Edges
              leading={leading}
              trailing={trailing}
              className={flexStyle({ overflowHidden })}
            >
              {title && (
                <Text
                  as="h2"
                  variant="displayS"
                  ellipsis
                  id={titleID}
                  className={titleStyle({ withClose: closeable })}
                >
                  {title}
                </Text>
              )}
              {description && (
                <div id={descriptionID} className="sr-only">
                  {description}
                </div>
              )}
              {children}
            </Edges>
          </div>
        </section>
        <Spacing height="s" />
      </Backdrop>
    </Portal>
  );
};
